import * as companyBillService from "../../../services/companyBill";
import * as customerBillService from "../../../services/customerBill";
import * as originPackageService from "../../../services/originPackage";
import * as originTransportationService from "../../../services/originTransportation";
import * as settings from "../../../services/settings";

import { ArrowLeftOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  notification,
} from "antd";
import { formatNumber, formatNumberVolume } from "../../../Components/model/formatNumber";
import { getRouteLabel, getTransportationStatusLabel, isEmpty } from "../../../utils/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AuthGroup from "../../../../configs/authgroup";
import { MenuContext } from "../../../Components/Layout/Admin";
import { ModalDelete } from "../../../Components/model/modal/delete";
import dayjs from "dayjs";
import permissions from "../../../../configs/permission";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { useContext } from "react";
import { usePermission } from "../../../hooks/usePermission";
import { useTranslation } from "react-i18next";

const { Link } = Typography;

const initPage = { limit: 10, page: 1 };
const EXPORT_ALL_MAX = 5000;
const toNumber = (value) => Number(String(value ?? "").replace(/,/g, ""));

/** ซ่อนจากผู้ใช้ที่ไม่ใช่ origin warehouse / superuser — ห้ามส่งไป API */
const stripRestrictedSearchParams = (params, canViewRestricted) => {
  if (canViewRestricted) return params;
  const next = { ...params };
  delete next.external_container;
  delete next.note;
  return next;
};

export const OriginPackageTransportationPage = () => {
  const { setSelectedMenuKeys, setOpenMenuKeys } = useContext(MenuContext);
  const { hasPermission, isSuperuser, hasGroup } = usePermission();
  const [form] = Form.useForm();
  const { accessToken } = useAccessToken();
  const hasInitialized = useRef(false);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation("originpackagetransportation", "buttons", "common", "options");
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(initPage);
  const [destinationPackageData, setDestinationPackageData] = useState([]);
  // const [ellipsis, setEllipsis] = useState(true)
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [prepareDeleteData, setPrepareDeleteData] = useState([]);
  const [countSelect, setCountSelect] = useState(0);
  const [listWareHouseOrigin, setListWareHouseOrigin] = useState([]);
  const [listWareHouseDestination, setListWareHouseDestination] = useState([]);

  const [isOpenButton, setIsOpenButton] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowsValues, setSelectedRowsValues] = useState([]);
  const navigate = useNavigate();
  const { Title } = Typography;
  const [total, setTotal] = useState(0);
  const { RangePicker } = DatePicker;
  const [dateRange, setDateRange] = useState([]);

  const [tempSearch, setTempSearch] = useState();

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({
        message,
        description,
        duration: 5,
      });
    },
    [api]
  );

  /** จำนวนรายการที่ถือว่าเลือก: แถวที่เลือกจริง หรือ count จาก Select all หรือ total */
  const getExportScopeCount = useCallback(() => {
    if (selectedRowKeys.length > 0) return toNumber(selectedRowKeys.length);
    if (countSelect > 0) return toNumber(countSelect);
    return toNumber(total);
  }, [selectedRowKeys, countSelect, total]);

  const fetchOriginTransportation = useCallback(
    async (param) => {
      try {
        const data = await originTransportationService.getOriginTransportation(accessToken, param);

        if (data) {
          setTotal(data.totalCount);
          setDestinationPackageData(data.queryOriginTransportation);
          setSearch({ ...param });
        }
      } catch (err) {
        console.error("[OriginPackageTransportationPage] fetchOriginTransportation: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
    },
    [accessToken]
  );

  const updateUrlParams = useCallback(
    (params) => {
      const newParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "" && value !== "all") {
          newParams.set(key, String(value));
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [setSearchParams]
  );

  const onSearch = useCallback(
    async (values) => {
      const {
        container,
        depart_at,
        destination_id,
        external_container,
        issue_bill_status,
        note,
        origin_id,
        route,
        transportation_status,
        transportation_id,
        ...data
      } = values;

      let start_date;
      let end_date;
      if (data && depart_at) {
        start_date = dateRange[0] || undefined;
        end_date = dateRange[1] || undefined;
      }

      const params = {
        depart_at_from: start_date,
        depart_at_to: end_date,
        container: container != undefined ? container.trim() : null,
        transportation_status: normalizeSelection(transportation_status),
        origin_id: normalizeSelection(origin_id),
        destination_id: normalizeSelection(destination_id),
        route: normalizeSelection(route),
        issue_bill_status: normalizeSelection(issue_bill_status),
        transportation_id: normalizeSelection(transportation_id),
        external_container: external_container != undefined ? external_container.trim() : null,
        note: note,
      };

      setCountSelect(0);
      setTempSearch(values);
      try {
        setLoading(true);
        await fetchOriginTransportation({
          ...params,
          page: values.page ?? initPage.page,
          limit: values.limit ?? initPage.limit,
        });
      } catch (err) {
        console.error("[OriginPackageTransportationPage] onSearch: ", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchOriginTransportation]
  );

  const normalizeSelection = (values) => {
    const modify = values !== "all" ? values : undefined;
    return modify;
  };

  const handleItemClick = async (item) => {
    const actionCountNum = getExportScopeCount();
    if (actionCountNum > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: actionCountNum.toLocaleString(),
        })
      );
      setCountSelect(EXPORT_ALL_MAX);
      if (selectedRowKeys.length > EXPORT_ALL_MAX) {
        setSelectedRowKeys((keys) => keys.slice(0, EXPORT_ALL_MAX));
        setSelectedRowsValues((rows) => rows.slice(0, EXPORT_ALL_MAX));
      }
      return;
    }
    switch (item) {
      case "checklist":
        exportCheckList();
        break;
      case "originTransportation":
        exportOriginTransportation();
        break;
      case "customerAccount":
        exportCustomer();
        break;
      case "customerAccountLand":
        exportCustomer("originTransport-land");
        break;
      case "customerAccountSea":
        exportCustomer("originTransport-sea");
        break;
      case "exportDestinationDeliveryExcel":
        exportDestinationDeliveryExcel();
        break;
    }
  };

  const exportCheckList = async () => {
    const exportCountNum = getExportScopeCount();
    if (exportCountNum > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: exportCountNum.toLocaleString(),
        })
      );
      setCountSelect(EXPORT_ALL_MAX);
      if (selectedRowKeys.length > EXPORT_ALL_MAX) {
        setSelectedRowKeys((keys) => keys.slice(0, EXPORT_ALL_MAX));
        setSelectedRowsValues((rows) => rows.slice(0, EXPORT_ALL_MAX));
      }
      return;
    }
    setLoading(true);
    const params = { page: 1, limit: 100000 };
    try {
      const request = {
        ids: selectedRowKeys.length ? JSON.stringify(selectedRowKeys) : [],
        ...params,
      };
      const response = await originTransportationService.exportCheckList(accessToken, request);

      // Extract filename from headers
      const contentDisposition = response.headers["content-disposition"];
      let filename = `checklist-${dayjs().format("YYYY-MM-DD")}`; // Fallback filename
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a link for downloading
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename; // Set the filename
      document.body.appendChild(link);
      link.click(); // Trigger download
      document.body.removeChild(link); // Cleanup
      URL.revokeObjectURL(link.href);

      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Export Excel Success"}</>);
    } catch (err) {
      console.error("[OriginPackageTransportationPage] exportCheckList: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const EXPORT_CUSTOMER_MAX = 500;

  const exportCustomer = async (prefixFilename = "OriginPackageTransportationCustoms") => {
    const exportCountNum = getExportScopeCount();

    // คำนวณ effectiveSelectedKeys ก่อน setState เพราะ setState เป็น async
    let effectiveSelectedKeys = selectedRowKeys;
    if (exportCountNum > EXPORT_CUSTOMER_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        <>{"คำเตือน"}</>,
        <>{`จำนวนข้อมูล (${exportCountNum.toLocaleString()} รายการ) เกินกำหนด ระบบจะ Export ${EXPORT_CUSTOMER_MAX.toLocaleString()} รายการแรก`}</>
      );
      setCountSelect(EXPORT_CUSTOMER_MAX);
      if (selectedRowKeys.length > EXPORT_CUSTOMER_MAX) {
        effectiveSelectedKeys = selectedRowKeys.slice(0, EXPORT_CUSTOMER_MAX);
        setSelectedRowKeys(effectiveSelectedKeys);
        setSelectedRowsValues((rows) => rows.slice(0, EXPORT_CUSTOMER_MAX));
      }
      // ไม่ return — ทำการ export ต่อเลยด้วย limit ที่ถูก cap แล้ว
    }

    setLoading(true);
    const values = search;
    let depart_at_from;
    let depart_at_to;
    if (values && (values?.depart_at || values?.depart_at_from || values?.depart_at_to)) {
      depart_at_from = dateRange[0] || undefined;
      depart_at_to = dateRange[1] || undefined;
    }

    const canViewRestricted = isSuperuser || hasGroup(AuthGroup.OriginWarehouse);
    const params = stripRestrictedSearchParams(
      {
        depart_at_from: depart_at_from,
        depart_at_to: depart_at_to,
        route: values.route ? values.route : undefined,
        destination_id: values.destination_id || undefined,
        origin_id: values.origin_id || undefined,
        container: values.container || undefined,
        transportation_status: values.transportation_status || undefined,
        issue_bill_status: values.issue_bill_status || undefined,
        external_container: values.external_container || undefined,
        note: values.note || undefined,
      },
      canViewRestricted
    );

    // ถ้า prefixFilename มี land ให้เป็น route land และถ้า prefixFilename มี sea ให้เป็น route sea
    if (prefixFilename.includes("land")) {
      params.route = "land";
    } else if (prefixFilename.includes("sea")) {
      params.route = "sea";
    }

    try {
      const exportLimit = EXPORT_CUSTOMER_MAX;
      const request = {
        ids: effectiveSelectedKeys.length ? JSON.stringify(effectiveSelectedKeys) : [],
        ...params,
        page: 1,
        limit: exportLimit,
      };
      const response = await originTransportationService.exportCustomer(accessToken, request);

      // Extract filename from headers
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${prefixFilename}-${dayjs().format("YYYY-MM-DD")}`; // Fallback filename
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a link for downloading
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename; // Set the filename
      document.body.appendChild(link);
      link.click(); // Trigger download
      document.body.removeChild(link); // Cleanup
      URL.revokeObjectURL(link.href);

      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"สำเร็จ"}</>, <>{"Export Excel สำเร็จ"}</>);
    } catch (err) {
      console.error("[OriginPackageTransportationPage] exportCustomer: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"เกิดข้อผิดพลาด"}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const exportOriginTransportation = async () => {
    const exportCountNum = getExportScopeCount();
    if (exportCountNum > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: exportCountNum.toLocaleString(),
        })
      );
      setCountSelect(EXPORT_ALL_MAX);
      if (selectedRowKeys.length > EXPORT_ALL_MAX) {
        setSelectedRowKeys((keys) => keys.slice(0, EXPORT_ALL_MAX));
        setSelectedRowsValues((rows) => rows.slice(0, EXPORT_ALL_MAX));
      }
      return;
    }
    setLoading(true);
    const values = search;
    let depart_at_from;
    let depart_at_to;
    if (values && (values?.depart_at || values?.depart_at_from || values?.depart_at_to)) {
      depart_at_from = dateRange[0] || undefined;
      depart_at_to = dateRange[1] || undefined;
    }

    const canViewRestricted = isSuperuser || hasGroup(AuthGroup.OriginWarehouse);
    const params = stripRestrictedSearchParams(
      {
        depart_at_from: depart_at_from,
        depart_at_to: depart_at_to,
        route: values.route ? values.route : undefined,
        destination_id: values.destination_id || undefined,
        origin_id: values.origin_id || undefined,
        container: values.container || undefined,
        transportation_status: values.transportation_status || undefined,
        issue_bill_status: values.issue_bill_status || undefined,
        external_container: values.external_container || undefined,
        note: values.note || undefined,
      },
      canViewRestricted
    );
    try {
      const exportLimit = Math.max(1, Math.min(Number(total) || exportCountNum, exportCountNum));
      const request = {
        ids: selectedRowKeys.length ? JSON.stringify(selectedRowKeys) : [],
        ...params,
        page: 1,
        limit: exportLimit,
      };
      const response = await originTransportationService.exportOriginTransport(accessToken, request);

      // Extract filename from headers
      const contentDisposition = response.headers["content-disposition"];
      let filename = `originTransport-${dayjs().format("YYYY-MM-DD")}`; // Fallback filename
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a link for downloading
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename; // Set the filename
      document.body.appendChild(link);
      link.click(); // Trigger download
      document.body.removeChild(link); // Cleanup
      URL.revokeObjectURL(link.href);

      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Export Excel Success"}</>);
    } catch (err) {
      console.error("[OriginPackageTransportationPage] exportOriginTransportation: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const exportDestinationDeliveryExcel = async () => {
    setLoading(true);

    const transportationId =
      selectedRowKeys.length === 1
        ? selectedRowKeys[0]
        : countSelect === 1 && toNumber(total) === 1 && destinationPackageData.length === 1
          ? destinationPackageData[0].id
          : undefined;

    if (transportationId == null) {
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`Please select only one row`}</>);
      setLoading(false);
      return;
    }

    const tid = Number(transportationId);
    const findStatus = destinationPackageData.find((item) => Number(item.id) === tid);
    if (!findStatus) {
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{"ERROR."}</>,
        <>{`Selected row is not on this page. Switch page or select the row again.`}</>
      );
      setLoading(false);
      return;
    }

    const status = String(findStatus.transportation_status || "").toUpperCase();
    if (status !== "CLOSE" && status !== "ARRIVED") {
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{"ERROR."}</>,
        <>{`Status to export is close or arrived`}</>
      );
      setLoading(false);
      return;
    }

    try {
      const request = {
        id: tid,
      };
      const response = await originTransportationService.exportDestinationDeliveryExcel(accessToken, request);

      // Extract filename from headers
      let filename = `destination-delivery-${dayjs().format("YYYY-MM-DD-HHmmss")}.zip`;
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches?.[1]) {
          filename = matches[1];
        }
      }

      // ✅ Create blob and trigger download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); // required for Firefox
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      openNotificationWithIcon(settings.NotificationType.SUCCESS, <>{"Success."}</>, <>{"Export Excel Success"}</>);
    } catch (err) {
      console.error("[OriginPackageTransportationPage] exportDestinationDeliveryExcel: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const onSelectAll = async () => {
    const totalCount = toNumber(total);
    if (totalCount > EXPORT_ALL_MAX) {
      openNotificationWithIcon(
        settings.NotificationType.WARNING,
        t("Warning", { ns: "common" }),
        t("Export_All_Limit_Error", {
          ns: "error",
          max: EXPORT_ALL_MAX.toLocaleString(),
          count: totalCount.toLocaleString(),
        })
      );
      setSelectedRowKeys([]);
      setSelectedRowsValues([]);
      setCountSelect(EXPORT_ALL_MAX);
      setIsOpenButton(true);
      return;
    }
    setSelectedRowKeys([]);
    setSelectedRowsValues([]);
    setCountSelect(totalCount);
    setIsOpenButton(true);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, value) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRowsValues(value);
      setCountSelect(value.length);
      setIsOpenButton(true);
    },
  };

  const handleUpdateStatusClick = async (action) => {
    setLoading(true);
    if (selectedRowKeys.length === 0) {
      setLoading(false);
      return setSelectedRowKeys([]);
    }
    const data = {
      ids: selectedRowKeys,
      newStatus: action,
    };

    try {
      const response = await originTransportationService.updateStatus(accessToken, data);

      if (response != undefined) {
        openNotificationWithIcon(
          settings.NotificationType.SUCCESS,
          <>{"Success."}</>,
          <>{"Update transportation status success"}</>
        );
      }

      fetchOriginTransportation(search);
      setSelectedRowKeys([]);
    } catch (err) {
      console.error("[OriginPackageTransportationPage] handleUpdateStatusClick: ", err);
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
    } finally {
      setLoading(false);
    }
  };

  const handlerCreateIssueBill = async (action) => {
    const successfulTransportIds = [];
    let totalCreated = 0;
    let totalSkipped = 0;

    const promises = selectedRowsValues.map(async (list) => {
      if (list.transportation_status !== "close" && list.transportation_status !== "arrived") {
        openNotificationWithIcon(
          settings.NotificationType.ERROR,
          <>{t("Create_Company_Bill_Error.Title")}</>,
          <>{t("Create_Company_Bill_Error.Status_Must_Close_Or_Arrived")}</>
        );
        return null;
      }
      if (action === "company_bill") {
        return createIssueCompanyBill(list.id);
      }
      return createIssueCustomerBill(list.id);
    });

    const results = await Promise.all(promises);
    results.forEach((result, index) => {
      if (!result || typeof result.created_count !== "number" || result.created_count <= 0) {
        return;
      }
      totalCreated += result.created_count;
      totalSkipped += result.skipped_count ?? 0;
      successfulTransportIds.push(selectedRowsValues[index].id);
    });

    if (totalCreated > 0) {
      const resultNs = action === "company_bill" ? "Issue_Bill_Result" : "Issue_Customer_Bill_Result";
      const successTitle = t(`${resultNs}.Title_Success`);
      const successDescription =
        totalSkipped > 0
          ? t(`${resultNs}.Created_With_Skipped`, {
              created: totalCreated,
              skipped: totalSkipped,
            })
          : t(`${resultNs}.Created`, { count: totalCreated });

      // ใช้ static notification — navigate จะ unmount หน้านี้ ทำให้ useNotification ไม่แสดง
      notification.success({
        message: successTitle,
        description: successDescription,
        duration: 5,
      });

      setOpenMenuKeys(["bill"]);
      if (action === "company_bill") {
        setSelectedMenuKeys(["companybill"]);
        navigate("/bill/companybill", {
          state: { originTransportationId: successfulTransportIds },
        });
      } else {
        setSelectedMenuKeys(["customerbill"]);
        navigate("/bill/customerbill", {
          state: { originTransportationId: successfulTransportIds },
        });
      }
      setSelectedRowKeys([]);
    }
  };

  const getCreateCompanyBillErrorKey = (message) => {
    if (!message || typeof message !== "string") return "Create_Company_Bill_Error.Fallback";
    const m = message.trim();
    if (m.includes("Duplicate company bill for company_id(s)"))
      return "Create_Company_Bill_Error.Duplicate_For_Company_Ids";
    if (m.includes("No packages found for this company in this transportation"))
      return "Create_Company_Bill_Error.No_Packages";
    if (m.includes("Duplicate company bill")) return "Create_Company_Bill_Error.Duplicate";
    if (m.includes("An error occurred while creating company bills")) return "Create_Company_Bill_Error.Generic";
    return "Create_Company_Bill_Error.Fallback";
  };

  const createIssueCompanyBill = async (id) => {
    setLoading(true);
    try {
      const body = {
        origin_transportation_id: id,
      };
      const response = await companyBillService.createCompanyBill(accessToken, body);
      setLoading(false);

      if (response?.response?.data) {
        const apiMessage = response.response?.data?.message ?? "";
        const errorKey = getCreateCompanyBillErrorKey(apiMessage);
        openNotificationWithIcon(
          settings.NotificationType.ERROR,
          <>{t("Create_Company_Bill_Error.Title")}</>,
          <>{t(errorKey)}</>
        );
        return null;
      }

      if (typeof response?.created_count === "number") {
        return {
          created_count: response.created_count,
          skipped_count: response.skipped_count ?? 0,
        };
      }

      if (response === true) {
        return { created_count: 1, skipped_count: 0 };
      }

      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{t("Create_Company_Bill_Error.Title")}</>,
        <>{t("Create_Company_Bill_Error.Fallback")}</>
      );
      return null;
    } catch (err) {
      console.error("[OriginPackageTransportationPage] createIssueCompanyBill: ", err);
      const { message } = settings.getErrorMessage(err);
      const errorKey = getCreateCompanyBillErrorKey(message);
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{t("Create_Company_Bill_Error.Title")}</>,
        <>{t(errorKey)}</>
      );
      setLoading(false);
      return null;
    }
  };

  const getCreateCustomerBillErrorKey = (message) => {
    if (!message || typeof message !== "string") return "Create_Customer_Bill_Error.Fallback";
    const m = message.trim();
    if (m.includes("No packages found for this company in this transportation"))
      return "Create_Customer_Bill_Error.No_Packages";
    if (m.includes("Duplicate customer bill")) return "Create_Customer_Bill_Error.Duplicate";
    if (m.includes("An error occurred while creating the customer bills"))
      return "Create_Customer_Bill_Error.Generic";
    return "Create_Customer_Bill_Error.Fallback";
  };

  const createIssueCustomerBill = async (id) => {
    setLoading(true);
    try {
      const body = {
        origin_transportation_id: id,
      };
      const response = await customerBillService.createCustomerBill(accessToken, body);
      setLoading(false);

      if (response?.response?.data) {
        const apiMessage = response.response?.data?.message ?? "";
        const errorKey = getCreateCustomerBillErrorKey(apiMessage);
        openNotificationWithIcon(
          settings.NotificationType.ERROR,
          <>{t("Create_Customer_Bill_Error.Title")}</>,
          <>{t(errorKey)}</>
        );
        return null;
      }

      if (typeof response?.created_count === "number") {
        return {
          created_count: response.created_count,
          skipped_count: response.skipped_count ?? 0,
        };
      }

      if (response === true) {
        return { created_count: 1, skipped_count: 0 };
      }

      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{t("Create_Customer_Bill_Error.Title")}</>,
        <>{t("Create_Customer_Bill_Error.Fallback")}</>
      );
      return null;
    } catch (err) {
      console.error("[OriginPackageTransportationPage] createIssueCustomerBill: ", err);
      const { message } = settings.getErrorMessage(err);
      const errorKey = getCreateCustomerBillErrorKey(message);
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{t("Create_Customer_Bill_Error.Title")}</>,
        <>{t(errorKey)}</>
      );
      setLoading(false);
      return null;
    }
  };

  const onFinish = async () => {
    const values = form.getFieldsValue();
    setLoading(true);
    try {
      await onSearch({ ...values, ...initPage });
    } finally {
      setLoading(false);
    }
  };
  /** External Container / Note ในการค้นหาและตาราง — เฉพาะ origin warehouse หรือ superuser */
  const canViewOriginWarehouseRestrictedFields = isSuperuser || hasGroup(AuthGroup.OriginWarehouse);

  const columns = [
    {
      title: t("Delete", { ns: "common" }),
      key: "delete",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Button
          disabled={
            !(
              isSuperuser ||
              hasGroup(AuthGroup.OriginWarehouse) ||
              hasPermission(permissions.OriginPackageTransportation.DELETE)
            )
          }
          type="link"
          onClick={() => {
            setIsOpenDelete(true);
            setPrepareDeleteData([record.id]);
          }}
        >
          <DeleteOutlined />
        </Button>
      ),
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Container"),
      dataIndex: "container",
      width: 200,
      render: (_, row) => (
        <p
          style={{ cursor: "pointer" }}
          onClick={() => {
            sessionStorage.setItem("originTransportationListParams", searchParams.toString());
            navigate("/package/originpackagetransportation/create", {
              state: { type: "EDIT", originTransportationData: row },
            });
          }}
        >
          {row.container}
        </p>
      ),
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_External_Container"),
      dataIndex: "external_container",
      key: "external_container",
      width: 200,
      render: (_, record) => <p>{record.external_container || "-"}</p>,
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Route"),
      width: 150,
      dataIndex: "route",
      render: (_, record) => {
        const label = getRouteLabel(record?.route, i18n);
        return <p>{label}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Origin"),
      width: 150,
      dataIndex: "app_warehouse_app_origintransportation_origin_idToapp_warehouse",
      render: (_, record) => {
        const origin = record.app_warehouse_app_origintransportation_origin_idToapp_warehouse
          ? record.app_warehouse_app_origintransportation_origin_idToapp_warehouse.warehouse_code
          : "-";
        return <p>{origin}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Destination"),
      width: 150,
      dataIndex: "app_warehouse_app_origintransportation_destination_idToapp_warehouse",
      render: (_, record) => {
        const destination = record.app_warehouse_app_origintransportation_destination_idToapp_warehouse
          ? record.app_warehouse_app_origintransportation_destination_idToapp_warehouse.warehouse_code
          : "-";
        return <p>{destination}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Depart_At"),
      dataIndex: "depart_at",
      key: "depart_at",
      width: 150,
      render: (_, record) => {
        const depart_at = record.depart_at != null ? dayjs(record.depart_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{depart_at}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Expect_Arrive_At"),
      dataIndex: "expect_arrive_at",
      width: 150,
      render: (_, record) => {
        const expect_arrive_at =
          record.expect_arrive_at != null ? dayjs(record.expect_arrive_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{expect_arrive_at}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Actual_Arrive_At"),
      dataIndex: "actual_arrive_at",
      width: 150,
      render: (_, record) => {
        const actual_arrive_at =
          record.actual_arrive_at != null ? dayjs(record.actual_arrive_at).format("YYYY-MM-DD hh:mm:ss A") : "-";
        return <p>{actual_arrive_at}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Total_Received"),
      dataIndex: "total_received",
      width: 150,
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Count"),
      dataIndex: "total_count",
      width: 150,
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Volume"),
      dataIndex: "volume",
      width: 150,
      render: (_, record) => formatNumberVolume(record.total_volume),
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Weight"),
      dataIndex: "weight",
      width: 150,
      render: (_, record) => formatNumber(record.total_weight),
    },
    {
      title: t("Origin_Package_Transportation_Table.Full_Container_Load"),
      dataIndex: "full_container_load",
      width: 150,
      render: (_, raw) => (raw.full_container_load ? "true" : "false"),
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Status"),
      dataIndex: "transportation_status",
      width: 150,
      render: (_, record) => {
        const label = getTransportationStatusLabel(record?.transportation_status, i18n);
        return <p>{label}</p>;
      },
    },
    {
      title: t("Origin_Package_Transportation_Table.Table_Note"),
      dataIndex: "note",
      key: "note",
      width: 200,
      render: (_, record) => <p>{record.note || "-"}</p>,
    },
    // {
    //   title: t("Origin_Package_Transportation_Table.Table_Issue_Company_Bill_Date"),
    //   dataIndex: "issue_company_bill_date",
    //   width: 150,
    //   render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    // },
    // {
    //   title: t("Origin_Package_Transportation_Table.Table_Issue_Customer_Bill_Date"),
    //   dataIndex: "issue_customer_bill_date",
    //   width: 150,
    //   render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    // },
    {
      title: t("Create_At", { ns: "common" }),
      dataIndex: "created_at",
      width: 150,
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    },
    {
      title: t("Update_At", { ns: "common" }),
      dataIndex: "updated_at",
      width: 150,
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm:ss A") : "-"),
    },
  ];

  const labelItems = [
    {
      title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Menu_Package.Title_Package", { ns: "menus" })}</span>,
    },
    {
      title: (
        <a href="/package/originpackagetransportation" style={{ fontWeight: 600, color: "black" }}>
          {t("Menu_Package.Origin_Package_Transportation", { ns: "menus" })}
        </a>
      ),
    },
  ];

  const hasRowSelection = selectedRowKeys.length > 0;
  const hasExportScope = hasRowSelection || countSelect > 0;

  const canEnableDestinationDeliveryExcel =
    selectedRowKeys.length === 1 || (countSelect === 1 && toNumber(total) === 1 && destinationPackageData.length === 1);

  const itemsExport = [
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Company_Account")}`,
      key: "companyAccount",
      disabled: true,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Origin_Transportation_Customs")}`,
      key: "customerAccount",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Origin_Transportation_Land")}`,
      key: "customerAccountLand",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Origin_Transportation_Sea")}`,
      key: "customerAccountSea",
      disabled: !isOpenButton ? true : false,
    },
    // {
    //   label: `${t("Origin_Package_Transportation_Function.Export.Select_Origin_Transportation")}`,
    //   key: "originTransportation",
    //   disabled: !isOpenButton ? true : false,
    // },
    // {
    //   label: `${t("Origin_Package_Transportation_Function.Export.Select_Origin_Transportation_Customs")}`,
    //   key: "originTransportationCustoms",
    //   disabled: true,
    // },
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Checklist")}`,
      key: "checklist",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Export.Select_Export_Destination_Delivery_Excel")}`,
      key: "exportDestinationDeliveryExcel",
      disabled: !isOpenButton ? true : false,
    },
  ];

  const itemsExportForMenu = itemsExport.map((item) => {
    if (!hasExportScope) {
      return { ...item, disabled: true };
    }
    if (item.key === "exportDestinationDeliveryExcel" && !canEnableDestinationDeliveryExcel) {
      return { ...item, disabled: true };
    }
    return item;
  });

  const itemsStatus = [
    {
      label: `${t("Origin_Package_Transportation_Function.Update_Status.Select_Open_Transportation")}`,
      key: "open",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Update_Status.Select_Close_Transportation")}`,
      key: "close",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Update_Status.Select_Delay_Transportation")}`,
      key: "delay",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Update_Status.Select_Arrive_Transportation")}`,
      key: "arrived",
      disabled: !isOpenButton ? true : false,
    },
  ];

  /** คลังปลายทางเท่านั้น (ไม่ใช่คลังต้นทาง): เปิด/ปิดการขนส่ง disable ตลอด — ล่าช้า/ถึงปลายทางเมื่อมีแถวที่เลือก */
  const isDestinationWarehouseOpenCloseDisabled =
    hasGroup(AuthGroup.DestinationWarehouse) && !isSuperuser && !hasGroup(AuthGroup.OriginWarehouse);

  const itemsStatusForMenu = itemsStatus.map((item) => {
    if (!hasRowSelection) {
      return { ...item, disabled: true };
    }
    if (isDestinationWarehouseOpenCloseDisabled && (item.key === "open" || item.key === "close")) {
      return { ...item, disabled: true };
    }
    return item;
  });

  const itemsIssueBill = [
    {
      label: `${t("Origin_Package_Transportation_Function.Issue_Bill.Select_Customer_Bill")}`,
      key: "customer_bill",
      disabled: !isOpenButton ? true : false,
    },
    {
      label: `${t("Origin_Package_Transportation_Function.Issue_Bill.Select_Company_Bill")}`,
      key: "company_bill",
      disabled: !isOpenButton ? true : false,
    },
  ];

  const itemsIssueBillForMenu = itemsIssueBill.map((item) => (!hasRowSelection ? { ...item, disabled: true } : item));

  const selectOriginPackageRoute = [
    { value: "all", label: t("Search_Form.Option_All", { ns: "originpackagetransportation" }) },
    { value: "sea", label: t("Search_Form.Option_Route_Sea", { ns: "originpackagetransportation" }) },
    { value: "land", label: t("Search_Form.Option_Route_Land", { ns: "originpackagetransportation" }) },
  ];

  const selectTransportationStatus = [
    { value: "all", label: t("Search_Form.Option_All", { ns: "originpackagetransportation" }) },
    { value: "open", label: t("Search_Form.Option_Transportation_Status_Open", { ns: "originpackagetransportation" }) },
    {
      value: "close",
      label: t("Search_Form.Option_Transportation_Status_Close", { ns: "originpackagetransportation" }),
    },
    {
      value: "delay",
      label: t("Search_Form.Option_Transportation_Status_Delay", { ns: "originpackagetransportation" }),
    },
    {
      value: "arrived",
      label: t("Search_Form.Option_Transportation_Status_Arrived", { ns: "originpackagetransportation" }),
    },
  ];

  const selectIssueBillStatus = [
    { value: "all", label: t("Search_Form.Option_All", { ns: "originpackagetransportation" }) },
    {
      value: 0,
      label: t("Search_Form.Select_Issue_Bill_Status.Issue_Bill_Company_And_Customer", {
        ns: "originpackagetransportation",
      }),
    },
    {
      value: 1,
      label: t("Search_Form.Select_Issue_Bill_Status.Issue_Bill_Company", { ns: "originpackagetransportation" }),
    },
    {
      value: 2,
      label: t("Search_Form.Select_Issue_Bill_Status.Issue_Bill_Customer", { ns: "originpackagetransportation" }),
    },
  ];

  const buildWarehouseOptions = (data) => {
    const origin = data.filter((d) => d.is_origin).map((d) => ({ value: d.id, label: d.warehouse_code }));

    const destination = data.filter((d) => d.is_destination).map((d) => ({ value: d.id, label: d.warehouse_code }));

    return { origin, destination };
  };
  const fetchWareHouseData = useCallback(
    async (param) => {
      try {
        const data = await originPackageService.getWareHouses(accessToken);
        const { origin, destination } = buildWarehouseOptions(data);
        const allLabel = t("Search_Form.Option_All", { ns: "originpackagetransportation" });
        setListWareHouseOrigin([{ label: allLabel, value: "all" }, ...origin]);
        setListWareHouseDestination([{ label: allLabel, value: "all" }, ...destination]);
      } catch (err) {
        console.error("[OriginPackageTransportationPage] fetchWareHouseData: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
    },
    [accessToken, t]
  );

  const disableFutureDates = useCallback((current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  }, []);

  const handleDateChange = useCallback((dates, dateStrings) => {
    setDateRange(dateStrings);
  }, []);

  const reset = useCallback(() => {
    form.resetFields();
    setCountSelect(0);
    fetchOriginTransportation({ ...initPage });
  }, [form, fetchOriginTransportation]);

  useEffect(() => {
    if (!accessToken || hasInitialized.current) return;
    hasInitialized.current = true;
    setLoading(true);

    (async () => {
      try {
        await fetchWareHouseData();
        await fetchOriginTransportation({ ...initPage });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const onDelete = async (data) => {
    const payload = { ids: data };
    try {
      await originTransportationService.deletePackageOriginTransportation(accessToken, payload);
      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        "Success.",
        "Delete Origin Package Transportation Success."
      );
      setPrepareDeleteData([]);
      setSelectedRowKeys([]);
      setSelectedRowsValues([]);
      setCountSelect(0);
      setIsOpenDelete(false);
      onSearch(initPage);
    } catch (err) {
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, "ERROR.", `${message}`);
      console.error("[OriginPackageTransportationPage] onDelete: ", err);
    }
  };

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <Breadcrumb items={labelItems}></Breadcrumb>
            <Title
              level={4}
              style={{
                textWrap: "nowrap",
                display: "flex",
                gap: "7px",
                padding: "15px 0px",
              }}
            >
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Menu_Package.Origin_Package_Transportation", { ns: "menus" })}
            </Title>
          </Col>
        </Row>
        <Row style={{ overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={form}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              layout="horizontal"
              onFinish={onFinish}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  form.submit();
                }
              }}
              initialValues={{
                depart_at: [dayjs().startOf("day"), dayjs().endOf("day")],
                container: undefined,
                transportation_status: "all",
                origin_id: "all",
                destination_id: "all",
                route: "all",
                issue_bill_status: "all",
                external_container: undefined,
                note: undefined,
              }}
            >
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item name="depart_at" label={t("Search_Form.Depart_At")}>
                    <RangePicker
                      allowEmpty
                      style={{ width: "100%", minWidth: 200 }}
                      onChange={handleDateChange}
                      disabledDate={disableFutureDates}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search_Form.Container")} name="container" key="container">
                    <Input placeholder={t("Search_Form.Container")} style={{ minWidth: 200 }} />
                  </Form.Item>
                </Col>
              </Row>
              {canViewOriginWarehouseRestrictedFields && (
                <Row>
                  <Col span={24} style={{ minWidth: 280 }}>
                    <Form.Item
                      label={t("Search_Form.External_Container")}
                      name="external_container"
                      key="external_container"
                    >
                      <Input placeholder={t("Search_Form.External_Container")} style={{ minWidth: 200 }} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    label={t("Search_Form.Transportation_Status")}
                    name="transportation_status"
                    key="transportation_status"
                  >
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={t("Search_Form.Transportation_Status")}
                      options={selectTransportationStatus}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search_Form.Origin")} name="origin_id" key="origin_id">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      options={listWareHouseOrigin}
                      placeholder={t("Search_Form.Origin")}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search_Form.Destination")} name="destination_id" key="destination_id">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={t("Search_Form.Destination")}
                      options={listWareHouseDestination}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item label={t("Search_Form.Route")} name="route" key="route">
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={t("Search_Form.Route")}
                      options={selectOriginPackageRoute}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24} style={{ minWidth: 280 }}>
                  <Form.Item
                    label={t("Search_Form.Issue_Bill_Status")}
                    name="issue_bill_status"
                    key="issue_bill_status"
                  >
                    <Select
                      style={{ width: "100%", minWidth: 200 }}
                      placeholder={t("Search_Form.Issue_Bill_Status")}
                      options={selectIssueBillStatus}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {canViewOriginWarehouseRestrictedFields && (
                <Row>
                  <Col span={24} style={{ minWidth: 280 }}>
                    <Form.Item label={t("Search_Form.Note")} name="note" key="note">
                      <Input placeholder={t("Search_Form.Note")} style={{ minWidth: 200 }} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: "end", whiteSpace: "nowrap" }}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit">
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => reset()} style={{ marginLeft: "5px" }}>
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ marginTop: 20 }}>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div>
                {(isSuperuser ||
                  hasGroup(AuthGroup.OriginWarehouse) ||
                  hasPermission(permissions.OriginPackageTransportation.DELETE)) && (
                  <Button
                    type="primary"
                    disabled={isEmpty(selectedRowKeys)}
                    onClick={() => {
                      setIsOpenDelete(true);
                      setPrepareDeleteData(selectedRowKeys);
                    }}
                  >
                    {t("Button_Origin_Package_Transportation.BTN_Delte_All")}
                  </Button>
                )}
              </div>
              <div className="ml-auto max-w-full">
                <Space direction="horizontal" wrap>
                  <Dropdown
                    menu={{
                      items: itemsExportForMenu.map((item) => ({
                        key: item.key,
                        label: item.label,
                        disabled: item.disabled,
                        onClick: () => {
                          handleItemClick(item.key);
                        },
                      })),
                    }}
                    placement="bottomLeft"
                  >
                    {(isSuperuser ||
                      hasGroup(AuthGroup.OriginWarehouse) ||
                      hasPermission(permissions.OriginPackageTransportation.EXPORT)) && (
                      <Button type="primary">
                        <Space>
                          {t("Origin_Package_Transportation_Function.Export.Title_Export")}
                          <DownOutlined />
                        </Space>
                      </Button>
                    )}
                  </Dropdown>
                  <Dropdown
                    menu={{
                      items: itemsStatusForMenu.map((item) => ({
                        key: item.key,
                        label: item.label,
                        disabled: item.disabled,
                        onClick: () => {
                          handleUpdateStatusClick(item.key);
                        },
                      })),
                    }}
                    placement="bottomLeft"
                  >
                    {(isSuperuser ||
                      hasGroup(AuthGroup.OriginWarehouse) ||
                      hasGroup(AuthGroup.DestinationWarehouse) ||
                      hasPermission(permissions.OriginPackageTransportation.UPDATE_STATUS)) && (
                      <Button type="primary">
                        <Space>
                          {t("Origin_Package_Transportation_Function.Update_Status.Title_Update_Status")}
                          <DownOutlined />
                        </Space>
                      </Button>
                    )}
                  </Dropdown>
                  <Dropdown
                    menu={{
                      items: itemsIssueBillForMenu.map((item) => ({
                        key: item.key,
                        label: item.label,
                        disabled: item.disabled,
                        onClick: () => {
                          handlerCreateIssueBill(item.key);
                        },
                      })),
                    }}
                    placement="bottomLeft"
                  >
                    {(isSuperuser ||
                      hasGroup(AuthGroup.OriginWarehouse) ||
                      hasGroup(AuthGroup.PlatformAccount1) ||
                      hasPermission(permissions.OriginPackageTransportation.ISSUE_BILL)) && (
                      <Button type="primary">
                        <Space>
                          {t("Origin_Package_Transportation_Function.Issue_Bill.Title_Issue_Bill")}
                          <DownOutlined />
                        </Space>
                      </Button>
                    )}
                  </Dropdown>
                  {(isSuperuser ||
                    hasGroup(AuthGroup.OriginWarehouse) ||
                    hasPermission(permissions.OriginPackageTransportation.ADD)) && (
                    <Button
                      type="primary"
                      onClick={() => {
                        sessionStorage.setItem("originTransportationListParams", searchParams.toString());
                        navigate("/package/originpackagetransportation/create", {
                          state: { type: "CREATE" },
                        });
                      }}
                    >
                      {t("Button_Origin_Package_Transportation.BTN_Add_Origin_Package_Transportation")}
                    </Button>
                  )}
                </Space>
              </div>
            </div>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Row align="middle" justify="center">
              <Col span={24}>
                {`${t("Select", { ns: "common" })}: ${countSelect} to ${total}`}&nbsp;
                <Link onClick={onSelectAll} target="_blank">
                  {t("Select_All", { ns: "common" })}
                </Link>
              </Col>
            </Row>
            <Card>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns.filter((column) =>
                  column.key === "note" || column.key === "external_container"
                    ? canViewOriginWarehouseRestrictedFields
                    : true
                )}
                dataSource={destinationPackageData}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                pagination={{
                  defaultPageSize: search.limit,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  current: search.page,
                  pageSize: search.limit,
                  total: total,
                  onChange: (page, limit) => {
                    onSearch({ ...tempSearch, page, limit });
                  },
                  showSizeChanger: true,
                }}
                style={{ cursor: "pointer" }}
              />
            </Card>
          </Col>
        </Row>
        <ModalDelete
          isOpen={isOpenDelete}
          handleCloseModal={() => setIsOpenDelete(false)}
          onClick={() => {
            onDelete(prepareDeleteData);
          }}
          dataDetail={prepareDeleteData}
        />
      </Spin>
    </>
  );
};
